import { TTranslator } from '@smartshell/ui-kit';
import dayjs from 'dayjs';

import { ELogType } from 'types';

import { TLocalesKeys } from '../localization';
import { LogDescriptionTemplate } from './interfaces';
import { sanitizeHTML } from './sanitize';
import { formatSum } from './utils';

const getTimeText = (value: number, t: TTranslator<TLocalesKeys>): string => {
  if (value < 60) {
    return `${value} ${t('second')}`;
  }

  const minutes = Math.floor(value / 60);
  const seconds = value % 60;

  if (minutes < 60) {
    return `${minutes} ${t('minute')} ${
      seconds ? `${seconds} ${t('second')}` : ''
    } `;
  }

  const hours = Math.floor(value / 3600);
  const remainingMinutes = Math.floor((value % 3600) / 60);

  return `${hours} ${t('hour')} ${
    remainingMinutes ? `${remainingMinutes} ${t('minute')}` : ''
  } `;
};

const getPaymentMethodText = (
  method: string | undefined,
  t: TTranslator<TLocalesKeys>
): string => {
  switch (method) {
    case 'DEPOSIT':
      return t('log.PAYMENT_CREATED.description.DEPOSIT');
    case 'CARD':
      return t('log.PAYMENT_CREATED.description.CARD');
    case 'CASH':
      return t('log.PAYMENT_CREATED.description.CASH');
    case 'BONUS':
      return t('log.PAYMENT_CREATED.description.BONUS');
    case 'COMPOSITE':
      return t('log.PAYMENT_CREATED.description.COMPOSITE');
    case 'CLOUD_PAYMENTS':
      return 'CLOUD_PAYMENTS';
    case 'POSTPAID':
      return t('log.PAYMENT_CREATED.description.POSTPAID');
    case 'TINKOFF_SBP':
      return t('log.PAYMENT_CREATED.description.TINKOFF_SBP');
    case 'KASPI_QR':
      return 'KASPI_QR';
    case 'KASPI_CARD':
      return 'KASPI_CARD';
    case 'KASPI_PAY':
      return 'Kaspi ONLINE';
    default:
      return t('log.PAYMENT_CREATED.description.unknown');
  }
};
const getOnlinePaymentMethodText = (
  method: string | undefined,
  t: TTranslator<TLocalesKeys>
): string => {
  switch (method) {
    case 'DEPOSIT':
      return t('log.PAYMENT_CREATED.description.DEPOSIT');
    case 'CARD':
      return t('log.PAYMENT_CREATED.description.CARD');
    case 'BONUS':
      return t('log.PAYMENT_CREATED.description.BONUS');
    case 'COMPOSITE':
      return t('log.PAYMENT_CREATED.description.COMPOSITE');
    case 'CLOUD_PAYMENTS':
      return 'CLOUD_PAYMENTS';
    case 'POSTPAID':
      return t('log.PAYMENT_CREATED.description.POSTPAID');
    case 'TINKOFF_SBP':
      return t('log.PAYMENT_CREATED.description.TINKOFF_SBP');
    case 'KASPI_QR':
      return 'KASPI_QR';
    case 'KASPI_CARD':
      return 'KASPI_CARD';
    case 'KASPI_PAY':
      return 'Kaspi ONLINE';
    default:
      return t('online.pay');
  }
};

export const getTitleTypeForPromocode = (
  type: string,
  t: TTranslator<TLocalesKeys>,
  variant?: 'primary' | 'secondary'
): string => {
  switch (type) {
    case 'DISCOUNT':
      return variant !== 'secondary'
        ? `<b>${t('discount')}</b>`
        : `${t('discount_2')}`;
    case 'DEPOSIT_VALUE':
      return variant !== 'secondary'
        ? `<b>${t('deposit.fill.title')}</b>`
        : `${t('deposit.fill.title')}`;
    case 'BONUS':
      return variant !== 'secondary'
        ? `<b>${t('payments.category.bonuses_2')}</b>`
        : `${t('payments.category.bonuses_2')}`;
    default:
      return type || '';
  }
};

export const logDescriptionTemplate = new Map<ELogType, LogDescriptionTemplate>(
  [
    [
      ELogType.ACHIEVEMENT_ASSIGNED,
      (row, t) => (
        <div
          dangerouslySetInnerHTML={{
            __html: t(
              'log.ACHIEVEMENT_ASSIGNED.description',
              `<b>«${row?.original?.comment}»</b>`
            ),
          }}
        />
      ),
    ],
    [
      ELogType.ACHIEVEMENT_CREATED,
      (row, t) => (
        <div
          dangerouslySetInnerHTML={{
            __html: t(
              'log.ACHIEVEMENT_CREATED.description',
              `<b>«${row?.original?.comment}»</b>`
            ),
          }}
        />
      ),
    ],
    [
      ELogType.ACHIEVEMENT_DELETED,
      (row, t) => (
        <div
          dangerouslySetInnerHTML={{
            __html: t(
              'log.ACHIEVEMENT_DELETED.description',
              `<b>«${row?.original?.comment}»</b>`
            ),
          }}
        />
      ),
    ],
    [
      ELogType.BONUS_PAYMENT_CREATED,
      (row, t) => {
        const paymentInfo = t(
          'log.BONUS_PAYMENT_CREATED.description',
          `<b>№${row?.original?.payment.id}</b>`,
          `<b>${row?.original?.payment?.value}</b>.`
        );

        const comment = row?.original?.comment
          ? t('log.comment', `<b>${row?.original?.comment}</b>`)
          : '';

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: `
            <span>${paymentInfo}</span>
            ${comment ? `</br><span>${comment}</span>` : ''}
          `,
            }}
          />
        );
      },
    ],
    [
      ELogType.BONUS_UPDATED,
      (row, t) => {
        const textMain = t(
          row?.original?.object && row?.original?.subject
            ? 'log.BONUS_UPDATED.description_2'
            : 'log.BONUS_UPDATED.description',
          `<span style="color: var(--text-secondary); text-decoration: line-through">${row?.original?.value1}</span> -> ${row?.original?.value2}`
        );

        const comment = row?.original?.comment
          ? t(
              'log.BONUS_UPDATED.description_3',
              `<b>${row.original?.comment}</b>`
            )
          : '';

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: `
            <span>${textMain}</span>
            ${comment ? `</br><span>${comment}</span>` : ''}
          `,
            }}
          />
        );
      },
    ],
    [
      ELogType.BOOKING_CANCELED,
      (row, t, currency) => {
        const isDefault = t(
          'log.BOOKING_CANCELED.description',
          `<b>${row?.original?.hosts
            ?.map((host) => `${host?.title}`)
            .join(', ')}</b>`,
          `<b>${dayjs(row?.original?.booking?.from).format(
            'DD.MM.YYYY HH:mm:ss'
          )}</b>`,
          `<b>${dayjs(row?.original?.booking?.to).format(
            'DD.MM.YYYY HH:mm:ss'
          )}</b>`
        );

        const isOnline = row?.original?.value1
          ? t(
              'log.BOOKING_CANCELED.description_3',

              `<b>№${row?.original?.booking?.id}</b>`,
              `<b>${row?.original?.hosts
                ?.map((host) => `[${host?.title}]`)
                .join(', ')}</b>`,
              `<b>${dayjs(row?.original?.booking?.from).format(
                'DD.MM HH:mm:ss'
              )}</b>`,
              `<b>${dayjs(row?.original?.booking?.to).format(
                'DD.MM HH:mm:ss'
              )}</b>`,
              `<b>${row?.original?.value1} ${currency?.sign}</b>`
            )
          : t(
              'log.BOOKING_CANCELED.description_2',

              `<b>№${row?.original?.booking?.id}</b>`,
              `<b>${row?.original?.hosts
                ?.map((host) => `[${host?.title}]`)
                .join(', ')}</b>`,
              `<b>${dayjs(row?.original?.booking?.from).format(
                'DD.MM HH:mm:ss'
              )}</b>`,
              `<b>${dayjs(row?.original?.booking?.to).format(
                'DD.MM HH:mm:ss'
              )}</b>`
            );

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: row?.original?.booking?.cost ? isOnline : isDefault,
            }}
          />
        );
      },
    ],
    [
      ELogType.BOOKING_FAILED_TO_START,
      (row, t, currency) => {
        const text = t(
          'log.BOOKING_FAILED_TO_START.description',
          `<b>№${row?.original?.booking?.id}</b>`,
          `<b>${dayjs(row?.original?.booking?.from).format(
            'DD.MM HH:mm:ss'
          )}</b>`,
          `<b>${dayjs(row?.original?.booking?.to).format(
            'DD.MM HH:mm:ss'
          )}</b>`,
          `<b>[${row?.original?.comment}]</b>`,
          `<b>[${row?.original?.booking?.cost} ${currency?.sign}]</b>`
        );
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: text,
            }}
          />
        );
      },
    ],
    [
      ELogType.BOOKING_CREATED,
      (row, t, currency) => {
        const isDefault = t(
          'log.BOOKING_CREATED.description',
          `<b>${row?.original?.hosts?.map((host) => host?.title)}</b>`,
          `<b>${dayjs(row?.original?.booking?.from).format(
            'DD.MM HH:mm:ss'
          )}</b>`,
          `<b>${dayjs(row?.original?.booking?.to).format('DD.MM HH:mm:ss')}</b>`
        );

        const isOnline = t(
          'log.BOOKING_CREATED.description_2',
          `<b>№${row?.original?.booking?.id}</b>`,
          `<b>${row?.original?.hosts
            ?.map((host) => `[${host?.title}]`)
            .join(', ')}</b>`,

          `<b>${dayjs(row?.original?.booking?.from).format(
            'DD.MM HH:mm:ss'
          )}</b>`,
          `<b>${dayjs(row?.original?.booking?.to).format(
            'DD.MM HH:mm:ss'
          )}</b>`,
          `<b>${row?.original?.value1} ${currency?.sign}</b>`
        );

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: `
            ${row?.original?.booking?.cost ? isOnline : isDefault}
          `,
            }}
          />
        );
      },
    ],
    [
      ELogType.BOOKING_UPDATED,
      (row, t) => {
        const bookingId = row?.original?.booking?.id ?? '-';
        const raw = row?.original?.additional_data ?? '{}';

        type D = {
          client_nickname_old?: string;
          client_nickname_new?: string;
          comment_new?: string;
          comment_old?: string;
          from_new?: string;
          from_old?: string;
          to_new?: string;
          to_old?: string;
          hosts_new?: string;
          hosts_old?: string;
        };

        let d: D = {};
        d = JSON.parse(raw);

        const val = (v: any, fallback = t('log.common.empty')): string =>
          v === null || v === undefined || v === '' ? fallback : String(v);

        const fmt = (s?: string): string => {
          if (!s) return '—';
          const DD = s.slice(8, 10);
          const MM = s.slice(5, 7);
          const hh = s.slice(11, 13);
          const mm = s.slice(14, 16);
          const ss = s.slice(17, 19);
          if (!DD || !MM || !hh || !mm || !ss) return s;
          return `${DD}.${MM} ${hh}:${mm}:${ss}`;
        };

        const strike = (text: string): string =>
          `<span style="color: var(--text-secondary); text-decoration: line-through;">${text}</span>`;
        const bold = (text: string): string => `<b>${text}</b>`;

        const hostsOld = val(d.hosts_old, '—');
        const hostsNew = val(d.hosts_new, '—');
        const hostsChanged = hostsOld !== hostsNew;

        // Заголовок
        let header = '';
        if (!hostsChanged) {
          // "изменено бронирование №2837 компьютера пк-1"
          const parts = hostsOld
            .split(',')
            .map((s) => s.trim())
            .filter(Boolean);
          header =
            parts.length > 1
              ? t(
                  'log.BOOKING_UPDATED.headline_with_hosts',
                  `№${bookingId}`,
                  hostsOld
                )
              : t(
                  'log.BOOKING_UPDATED.headline_with_host',
                  `№${bookingId}`,
                  hostsOld
                );
        } else {
          // "изменено бронирование №2837"
          header = t(
            'log.BOOKING_UPDATED.headline_with_id_plain',
            `№${bookingId}`
          );
        }

        // Список изменённых полей (inline, через запятую)
        const chunks: string[] = [];

        // Компьютер
        if (hostsChanged) {
          chunks.push(
            t(
              'log.BOOKING_UPDATED.hosts_inline',
              strike(hostsOld),
              bold(hostsNew)
            )
          );
        }

        // Время
        if (d.from_old !== d.from_new || d.to_old !== d.to_new) {
          const oldRange = t(
            'log.BOOKING_UPDATED.time_range',
            fmt(d.from_old),
            fmt(d.to_old)
          ); // "с %s по %s"
          const newRange = t(
            'log.BOOKING_UPDATED.time_range',
            fmt(d.from_new),
            fmt(d.to_new)
          );
          chunks.push(
            t(
              'log.BOOKING_UPDATED.time_inline',
              strike(oldRange),
              bold(newRange)
            )
          );
        }

        // Пользователь
        if (
          val(d.client_nickname_old, '0') !== val(d.client_nickname_new, '0')
        ) {
          const renderClient = (id: string): string =>
            id === '0'
              ? t('log.BOOKING_UPDATED.guest')
              : t('log.BOOKING_UPDATED.client_plain', id, ''); // телефон неизвестен → пусто
          chunks.push(
            t(
              'log.BOOKING_UPDATED.user_inline',
              strike(renderClient(val(d.client_nickname_old, '0'))),
              bold(renderClient(val(d.client_nickname_new, '0')))
            )
          );
        }

        // Комментарий
        if (val(d.comment_old, '') !== val(d.comment_new, '')) {
          chunks.push(
            t(
              'log.BOOKING_UPDATED.comment_inline',
              strike(val(d.comment_old, t('log.common.empty'))),
              bold(val(d.comment_new, t('log.common.empty')))
            )
          );
        }

        const suffix = chunks.length ? `: ${chunks.join(', ')}` : '';
        return (
          <span dangerouslySetInnerHTML={{ __html: `${header}${suffix}` }} />
        );
      },
    ],
    [
      ELogType.CASH_ORDER_CREATED,
      (row, t, currency) => {
        const type = row?.original?.cash_order?.type;
        const comment = row?.original?.cash_order?.comment;
        const commentText = `<b>${row?.original?.cash_order?.comment}</b>`;
        const sumText = `<b>${row?.original?.cash_order?.sum} ${currency.sign}</b>`;

        const getMainText = (
          type: 'PKO' | 'RKO',
          comment: string | undefined
        ): string => {
          if (type === 'RKO' && comment) {
            return t(
              'log.CASH_ORDER_CREATED.description_4',
              sumText,
              commentText
            );
          } else if (type === 'RKO' && !comment) {
            return t('log.CASH_ORDER_CREATED.description_3', sumText);
          } else if (type === 'PKO' && comment) {
            return t(
              'log.CASH_ORDER_CREATED.description_2',
              sumText,
              commentText
            );
          } else {
            return t('log.CASH_ORDER_CREATED.description', sumText);
          }
        };

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: getMainText(type, comment),
            }}
          />
        );
      },
    ],
    [
      ELogType.CLIENT_AUTHORIZED_FROM_HOST,
      (_, t) => (
        <div
          dangerouslySetInnerHTML={{
            __html: t('log.CLIENT_AUTHORIZED_FROM_HOST.description'),
          }}
        />
      ),
    ],
    [
      ELogType.CLIENT_SESSION_UNPAUSED_AUTO,
      (row, t) => {
        const sessionId = `<b>№${row?.original?.client_session.id}</b>`;
        const client = row?.original?.client;
        const clientText = `<b>${client?.nickname ? client?.nickname : ''} (${
          client?.phone
        })</b>`;
        const finishAtDay = `${dayjs(
          row?.original?.client_session?.finished_at
        ).format('DD.MM')}`;
        const finishAtTime = `<b>${dayjs(
          row?.original?.client_session?.finished_at
        ).format('HH:mm:ss')}</b>`;
        const timeLeft = `<b>${getTimeText(row?.original?.value1, t)}</b>`;

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t(
                'log.CLIENT_SESSION_UNPAUSED_AUTO.description',
                sessionId,
                clientText,
                timeLeft,
                finishAtDay,
                finishAtTime
              ),
            }}
          />
        );
      },
    ],
    [
      ELogType.CLIENT_SESSION_UNPAUSED,
      (row, t) => {
        const sessionId = `<b>№${row?.original?.client_session.id}</b>`;
        const client = row?.original?.client;
        const clientText = `<b>${client?.nickname ? client?.nickname : ''} (${
          client?.phone
        })</b>`;
        const finishAtDay = `${dayjs(
          row?.original?.client_session?.finished_at
        ).format('DD.MM')}`;
        const finishAtTime = `<b>${dayjs(
          row?.original?.client_session?.finished_at
        ).format('HH:mm:ss')}</b>`;

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t(
                'log.CLIENT_SESSION_UNPAUSED.description_1',
                sessionId,
                clientText,
                finishAtDay,
                finishAtTime
              ),
            }}
          />
        );
      },
    ],
    [
      ELogType.CLIENT_SESSION_FINISHED,
      (row, t, currency) => {
        const isClient = Boolean(row?.original?.subject?.uuid);
        const isPerMinute =
          row?.original?.client_session?.is_per_minute === true;
        const sessionId = `<b>№${row?.original?.client_session.id}</b>`;
        const client = row?.original?.client;
        const deposit = row?.original?.deposit;
        const clientText = `<b>${client?.nickname ? client?.nickname : ''} (${
          client?.phone
        })</b>`;
        const clientText_2 = `<b>${client?.nickname ? client?.nickname : ''} (${
          client?.phone
        }, ${deposit}${currency?.sign})</b>`;
        const totalCost = `${t(
          'log.CLIENT_SESSION_FINISHED.description_2',
          `<b>${row?.original?.client_session?.total_cost} ${currency?.sign}</b>`
        )}`;
        const totalDuration = `<b>${getTimeText(
          row?.original?.client_session?.elapsed,
          t
        )}</b>`;
        const textMain = isClient
          ? t(
              'log.CLIENT_SESSION_FINISHED.description',
              sessionId,
              `${isPerMinute ? clientText_2 : clientText}`,
              totalDuration
            )
          : t(
              'log.CLIENT_SESSION_FINISHED.description.guest',
              sessionId,
              totalDuration
            );

        const textBonus = t(
          'log.CLIENT_SESSION_FINISHED.description_3',
          `<b>[${row?.original?.value1}]</b> ⊙.`
        );
        const comment = row?.original?.comment;
        const getComment = (comment: null | string): string => {
          switch (comment) {
            case 'OperatorTerminated':
              return `${t('log.CLIENT_SESSION_FINISHED.description_1')} <b>${t(
                'log.CLIENT_SESSION_FINISHED.description.operator'
              )}</b>`;
            case 'SessionTimeout':
              return `${t('log.CLIENT_SESSION_FINISHED.description_1')} <b>${t(
                'log.CLIENT_SESSION_FINISHED.description.sessionTimeout'
              )}</b>`;
            case 'ClientCancelled':
              return `${t('log.CLIENT_SESSION_FINISHED.description_1')} <b>${t(
                'log.CLIENT_SESSION_FINISHED.description.clientCancelled'
              )}</b>`;
            case 'ConnectionIssues':
              return `${t('log.CLIENT_SESSION_FINISHED.description_1')} <b>${t(
                'log.CLIENT_SESSION_FINISHED.description.connectionIssues'
              )}</b>`;
            case 'InsufficientFunds':
              return `${t('log.CLIENT_SESSION_FINISHED.description_1')} <b>${t(
                'log.CLIENT_SESSION_FINISHED.description.insufficientFunds'
              )}</b>`;
            case 'PaymentRefund':
              return `${t('log.CLIENT_SESSION_FINISHED.description_1')} <b>${t(
                'log.CLIENT_SESSION_FINISHED.description.paymentRefund'
              )}</b>`;
            case 'Penalty':
              return `${t('log.CLIENT_SESSION_FINISHED.description_1')} <b>${t(
                'log.CLIENT_SESSION_FINISHED.description.penalty'
              )}</b>`;
            case 'BoughtPausable':
              return `${t('log.CLIENT_SESSION_FINISHED.description_1')} <b>${t(
                'log.CLIENT_SESSION_FINISHED.description.boughtPausable'
              )}</b>`;
            case 'SessionExpired':
              return `${t('log.CLIENT_SESSION_FINISHED.description_1')} <b>${t(
                'log.CLIENT_SESSION_FINISHED.description.SessionExpired'
              )}</b>`;
            case 'BookingStarted':
              return `${t('log.CLIENT_SESSION_FINISHED.description_1')} <b>${t(
                'log.CLIENT_SESSION_FINISHED.description.BookingStarted'
              )}</b>`;
            case 'PausableTerminated':
              return `${t('log.CLIENT_SESSION_FINISHED.description_1')} <b>${t(
                'log.CLIENT_SESSION_FINISHED.description.PausableTerminated'
              )}</b>`;
            default:
              return '';
          }
        };

        let html = '';

        if (isPerMinute && !isClient) {
          html = `
          ${t(
            'log.CLIENT_SESSION_FINISHED.description.post',
            `<b>№${row?.original?.client_session?.id}</b>`
          )}
            </br>
          ${t(
            'log.CLIENT_SESSION_FINISHED.description.post_1',
            `<b>${getTimeText(row?.original?.client_session?.elapsed, t)}</b>`
          )}
          </br>
          ${t(
            'log.CLIENT_SESSION_FINISHED.description.post_2',
            `<b>${row?.original?.client_session?.total_cost}</b>`,
            `<b>${currency?.sign}</b>`
          )}
          </br>
          ${comment ? getComment(comment) : ''}
          `;
        } else {
          html = `
            <span>${textMain}</span>${
            row?.original?.client_session?.total_cost ? totalCost : ''
          }
            ${row?.original?.value1 ? textBonus : ''}
            ${comment ? getComment(comment) : ''}
          `;
        }
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: html,
            }}
          />
        );
      },
    ],
    [
      ELogType.CLIENT_SESSION_PAUSED,
      (row, t) => {
        const sessionId = `<b>№${row?.original?.client_session.id}</b>`;
        const client = row?.original?.client;
        const clientText = `<b>${client?.nickname ? client?.nickname : ''} (${
          client?.phone
        })</b>`;
        const timeLeft = `<b>${getTimeText(row?.original?.value1, t)}</b>`;
        const finishAtDay = `${dayjs(
          row?.original?.client_session?.finished_at
        ).format('DD.MM')}`;
        const finishAtTime = `<b>${dayjs(
          row?.original?.client_session?.finished_at
        ).format('HH:mm:ss')}</b>`;
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t(
                'log.CLIENT_SESSION_PAUSED.description',
                sessionId,
                clientText,
                timeLeft,
                finishAtDay,
                finishAtTime
              ),
            }}
          />
        );
      },
    ],
    [
      ELogType.CLUB_CREATED,
      (_, t) => (
        <div
          dangerouslySetInnerHTML={{
            __html: t('log.CLUB_CREATED.description'),
          }}
        />
      ),
    ],
    [
      ELogType.COMBO_CREATED,
      (row, t, currency) => {
        const positionsHtml = `
      ${t('positions')}
      </br>
      </br>
      <ul style="margin: 0; padding-left: 25px">
        ${row?.original?.combo_items
          ?.map((item) => `<li><b>${item?.title}</b></li>`)
          .join('')}
      </ul>
    `;
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t(
                'log.COMBO_CREATED.description',

                `<b>«${row?.original?.combo?.title}»</b></br>`,

                `${
                  row?.original?.combo?.host_group_title
                    ? `<b>«${row?.original?.combo?.host_group_title}»</b></br>`
                    : `<b>${t('all.groups')}</b></br>`
                }`,

                `${
                  row?.original?.combo?.tariff_title
                    ? `${t('tariff')}: <b>"${
                        row?.original?.combo?.tariff_title
                      }"</b></br>`
                    : `<span></span>`
                }`,
                `</br>${positionsHtml}</br></br>`,

                `<b>${row?.original?.value1}</b>`,
                `<b>${currency.sign}</b>`
              ),
            }}
          />
        );
      },
    ],
    [
      ELogType.COMBO_UPDATED,
      (row, t, currency) => {
        const positionsHtml = `
      ${t('positions')}</br></br>
      <ul style="margin: 0; padding-left: 25px">
        ${row?.original?.combo_items
          ?.map((item) => `<li><b>${item?.title}</b></li>`)
          .join('')}
      </ul>
    `;
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t(
                'log.COMBO_UPDATED.description',

                `<b>«${row?.original?.combo?.title}»</b></br>`,

                `${
                  row?.original?.combo?.host_group_title
                    ? `<b>«${row?.original?.combo?.host_group_title}»</b></br>`
                    : `<b>${t('all.groups')}</b></br>`
                }`,

                `${
                  row?.original?.combo?.tariff_title
                    ? `${t('tariff')}: <b>"${
                        row?.original?.combo?.tariff_title
                      }"</b></br>`
                    : `<span></span>`
                }`,
                `</br>${positionsHtml}</br></br>`,

                `<b>→ ${row?.original?.value1}</b>`,
                `<b>${currency.sign}</b>`
              ),
            }}
          />
        );
      },
    ],
    [
      ELogType.COMBO_DELETED,
      (row, t, currency) => {
        const positionsHtml = `
      ${t('positions')}</br></br>
      <ul style="margin: 0; padding-left: 25px">
        ${row?.original?.combo_items
          ?.map((item) => `<li><b>${item?.title}</b></li>`)
          .join('')}
      </ul>
    `;
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t(
                'log.COMBO_DELETED.description',

                `<b>«${row?.original?.combo?.title}»</b></br>`,

                `${
                  row?.original?.combo?.host_group_title
                    ? `<b>«${row?.original?.combo?.host_group_title}»</b></br>`
                    : `<b>${t('all.groups')}</b></br>`
                }`,

                `${
                  row?.original?.combo?.tariff_title
                    ? `${t('tariff')}: <b>"${
                        row?.original?.combo?.tariff_title
                      }"</b></br>`
                    : `<span></span>`
                }`,
                `</br>${positionsHtml}</br></br>`,

                `<b>${row?.original?.value1}</b>`,
                `<b>${currency.sign}</b>`
              ),
            }}
          />
        );
      },
    ],
    [
      ELogType.DISCOUNT_CREATED,
      (row, t) => {
        const discName = `<b>${row?.original?.discount?.title}</b>`;
        const discValue = `<b>${row?.original?.discount?.value}%</b>`;
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t(
                'log.DISCOUNT_CREATED.description',
                discName,
                discValue
              ),
            }}
          />
        );
      },
    ],
    [
      ELogType.DISCOUNT_DELETED,
      (row, t) => {
        const discName = `<b>${row?.original?.discount?.title}</b>`;
        const discValue = `<b>${row?.original?.discount?.value}%</b>`;

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t(
                'log.DISCOUNT_DELETED.description',
                discName,
                discValue
              ),
            }}
          />
        );
      },
    ],
    [
      ELogType.DISCOUNT_UPDATED,
      (row, t) => {
        const discName = `<b>${row?.original?.discount?.title}</b>`;
        const discValue = `<b>${row?.original?.discount?.value}%</b>`;
        const discValue2 = `<span style="color: var(--text-secondary); text-decoration: line-through">${row?.original?.value1}%</span>`;

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t(
                'log.DISCOUNT_UPDATED.description',
                discName,
                discValue2,
                discValue
              ),
            }}
          />
        );
      },
    ],
    [
      ELogType.GOOD_CATEGORY_CREATED,
      (row, t) => {
        const goodsGroupName = `<b>${row?.original?.goods_category?.title}</b>`;
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t(
                'log.GOOD_CATEGORY_CREATED.description',
                goodsGroupName
              ),
            }}
          />
        );
      },
    ],
    [
      ELogType.GOOD_CATEGORY_UPDATED,
      (row, t) => {
        const goodsGroupName = `<b>${row?.original?.goods_category?.title}</b>`;
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t(
                'log.GOOD_CATEGORY_UPDATED.description',
                goodsGroupName
              ),
            }}
          />
        );
      },
    ],
    [
      ELogType.GOOD_CATEGORY_DELETED,
      (row, t) => {
        const goodsGroupName = `<b>${row?.original?.goods_category?.title}</b>`;
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t(
                'log.GOOD_CATEGORY_DELETED.description',
                goodsGroupName
              ),
            }}
          />
        );
      },
    ],
    [
      ELogType.DEBT_ACTIVATED,
      (_, t) => (
        <div
          dangerouslySetInnerHTML={{
            __html: t('log.DEBT_ACTIVATED.description'),
          }}
        />
      ),
    ],
    [
      ELogType.GOOD_CREATED,
      (row, t) => {
        const goodName = `<b>${row?.original?.good?.title}</b>`;
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t('log.GOOD_CREATED.description', goodName),
            }}
          />
        );
      },
    ],
    [
      ELogType.GOOD_UPDATED,
      (row, t) => {
        const goodName = `<b>${row?.original?.good?.title}</b>`;
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t('log.GOOD_UPDATED.description', goodName),
            }}
          />
        );
      },
    ],
    [
      ELogType.GOOD_DELETED,
      (row, t) => {
        const goodName = `<b>${row?.original?.good?.title}</b>`;
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t('log.GOOD_DELETED.description', goodName),
            }}
          />
        );
      },
    ],
    [
      ELogType.HOST_CREATED,
      (row, t) => {
        const hostName = `<b>${row?.original?.host}</b>`;
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t('log.HOST_CREATED.description', hostName),
            }}
          />
        );
      },
    ],
    [
      ELogType.HOST_GROUP_CREATED,
      (row, t) => {
        const hostGroupName = `<b>${row?.original?.host_group.title}</b>`;
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t('log.HOST_GROUP_CREATED.description', hostGroupName),
            }}
          />
        );
      },
    ],
    [
      ELogType.HOST_GROUP_DELETED,
      (row, t) => {
        const hostGroupName = `<b>${row?.original?.host_group.title}</b>`;
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t('log.HOST_GROUP_DELETED.description', hostGroupName),
            }}
          />
        );
      },
    ],
    [
      ELogType.HOST_HARDWARE_CHANGED,
      (row, t) => {
        const devices = row?.original?.new_devices || [];

        const deviceLabels: Record<string, string> = {
          processor: 'CPU',
          video: 'GPU',
          ram: 'RAM',
          disk: 'Disc',
        };

        const mainText = devices
          .map((item) => {
            const label = deviceLabels[item.alias] || item.alias;
            return `<span>${label}: <span style="color: var(--text-secondary); text-decoration: line-through">${item?.value}</span><b>→</b><b>${item.value2}</b></span>`;
          })
          .join(' ');

        return (
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
            dangerouslySetInnerHTML={{
              __html: t(
                'log.HOST_HARDWARE_CHANGED.description',
                mainText ? `${mainText}` : ''
              ),
            }}
          />
        );
      },
    ],
    [
      ELogType.HOST_HARDWARE_APPLIED,
      (row, t) => {
        const devices = row?.original?.new_devices || [];

        const deviceLabels: Record<string, string> = {
          processor: 'CPU',
          video: 'GPU',
          ram: 'RAM',
          disk: 'Disc',
        };

        const mainText = devices
          .map((item) => {
            const label = deviceLabels[item.alias] || item.alias;
            return `<span>${label}: <b>${item.value2}</b></span>`;
          })
          .join(' ');

        return (
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
            dangerouslySetInnerHTML={{
              __html: t(
                'log.HOST_HARDWARE_APPLIED.description',
                mainText ? `${mainText}` : ''
              ),
            }}
          />
        );
      },
    ],
    [
      ELogType.HOST_PUNISHED,
      (row, t) => {
        const mainText =
          row?.original?.subject?.uuid && row?.original?.object?.uuid
            ? t(
                'log.HOST_PUNISHED.description.client',
                `<b>№${row.original?.client_session?.id}</b>`,
                `<b>${getTimeText(row?.original?.penalty, t)}</b>`
              )
            : t(
                'log.HOST_PUNISHED.description.guest',
                `<b>№${row.original?.client_session?.id}</b>`,
                `<b>${getTimeText(row?.original?.penalty, t)}</b>`
              );

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: mainText,
            }}
          />
        );
      },
    ],
    [
      ELogType.WORKER_CREATED,
      (row, t) => {
        const role = row?.original?.role;

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t('log.WORKER_CREATED.description', `<b>${role}</b>`),
            }}
          />
        );
      },
    ],
    [
      ELogType.WORKER_DELETED,
      (row, t) => {
        const role = row?.original?.role;

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t('log.WORKER_DELETED.description', `<b>${role}</b>`),
            }}
          />
        );
      },
    ],
    [
      ELogType.WORKER_UPDATED,
      (row, t) => {
        const role1 = row?.original?.role;
        const role2 = row?.original?.role2;

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: `
                <span>${t('log.WORKER_UPDATED.description')}</span>
                ${
                  role2
                    ? t(
                        'log.WORKER_UPDATED.description_2',
                        `<span style="color: var(--text-secondary); text-decoration: line-through">${role1}</span>-><b>${role2}</b>`
                      )
                    : ''
                }
              `,
            }}
          />
        );
      },
    ],
    [
      ELogType.WORKER_AUTHORIZED_FROM_HOST,
      (_, t) => (
        <div
          dangerouslySetInnerHTML={{
            __html: t('log.CLIENT_AUTHORIZED_FROM_HOST.description'),
          }}
        />
      ),
    ],
    [
      ELogType.WORKER_AUTHORIZED_FROM_BILLING,
      (_, t) => (
        <div
          dangerouslySetInnerHTML={{
            __html: t('log.WORKER_AUTHORIZED_FROM_BILLING.description'),
          }}
        />
      ),
    ],
    [
      ELogType.WORK_SHIFT_STARTED,
      (row, t, currency) => (
        <div
          dangerouslySetInnerHTML={{
            __html: t(
              'log.WORK_SHIFT_STARTED.description',
              `<b>${row?.original?.operator?.first_name} ${row?.original?.operator?.last_name}</b>`,
              `<b>${formatSum(row?.original?.cash_on_start, currency.sign)}</b>`
            ),
          }}
        />
      ),
    ],
    [
      ELogType.WORK_SHIFT_FINISHED,
      (row, t) => (
        <div
          dangerouslySetInnerHTML={{
            __html: t(
              'log.WORK_SHIFT_FINISHED.description',
              `<b>${row?.original?.operator?.first_name} ${row?.original?.operator?.last_name}</b>`
            ),
          }}
        />
      ),
    ],
    [
      ELogType.USER_UNBANNED,
      (row, t) => (
        <div
          dangerouslySetInnerHTML={{
            __html: t('log.USER_UNBANNED.description'),
          }}
        />
      ),
    ],
    [
      ELogType.USER_BANNED,
      (row, t) => (
        <div
          dangerouslySetInnerHTML={{
            __html: t('log.USER_BANNED.description'),
          }}
        />
      ),
    ],
    [
      ELogType.USER_RESET_PASSWORD,
      (row, t) => (
        <div
          dangerouslySetInnerHTML={{
            __html: t('log.USER_RESET_PASSWORD.description'),
          }}
        />
      ),
    ],
    [
      ELogType.PROMO_CODE_CREATED,
      (row, t, currency) => {
        const promo = row?.original?.promo_code_item;

        let type = '';
        let value = '';

        if (promo?.value) {
          try {
            const valueData = JSON.parse(promo.value);
            type = getTitleTypeForPromocode(valueData.type, t);

            if (valueData.type === 'DISCOUNT') {
              value = `<b>${valueData.value}%</b>`;
            } else if (valueData.type === 'DEPOSIT_VALUE') {
              value = `<b>${valueData.value}${currency.sign}</b>`;
            } else {
              value = `<b>${valueData.value}⊙</b>`;
            }
          } catch (e) {
            console.error('Error parsing promo value', e);
          }
        }

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t(
                'log.PROMO_CODE_CREATED.description',
                `«<b>${promo?.title || ''}</b>»`,
                type,
                value
              ),
            }}
          />
        );
      },
    ],
    [
      ELogType.PROMO_CODE_UPDATED,
      (row, t, currency) => {
        const promo = row?.original?.promo_code_item;

        let type = '';
        let value = '';

        if (promo?.value) {
          try {
            const valueData = JSON.parse(promo.value);
            type = getTitleTypeForPromocode(valueData.type, t);

            if (valueData.type === 'DISCOUNT') {
              value = `<b>${valueData.value}%</b>`;
            } else if (valueData.type === 'DEPOSIT_VALUE') {
              value = `<b>${valueData.value}${currency.sign}</b>`;
            } else {
              value = `<b>${valueData.value}⊙</b>`;
            }
          } catch (e) {
            console.error('Error parsing promo value', e);
          }
        }

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t(
                'log.PROMO_CODE_UPDATED.description',
                `«<b>${promo?.title || ''}</b>»`,
                type,
                value
              ),
            }}
          />
        );
      },
    ],
    [
      ELogType.PROMO_CODE_DELETED,
      (row, t) => (
        <div
          dangerouslySetInnerHTML={{
            __html: t(
              'log.PROMO_CODE_DELETED.description',
              `<b>${row?.original?.promo_code_item?.title}</b>`
            ),
          }}
        />
      ),
    ],
    [
      ELogType.PROMO_CAMPAIGN_CREATED,
      (row, t, currency) => {
        const campaign = row?.original?.promo_campaign;

        let type = '';
        let value = '';

        if (campaign?.value) {
          try {
            const valueData = JSON.parse(campaign.value);
            type = getTitleTypeForPromocode(valueData.type, t);

            if (valueData.type === 'DISCOUNT') {
              value = `<b>${valueData.value}%</b>`;
            } else if (valueData.type === 'DEPOSIT_VALUE') {
              value = `<b>${valueData.value}${currency.sign}</b>`;
            } else {
              value = `<b>${valueData.value}⊙</b>`;
            }
          } catch (e) {
            console.error('Error parsing promo value', e);
          }
        }

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t(
                'log.PROMO_CAMPAIGN_CREATED.description',
                `«<b>${campaign?.title || ''}</b>»`,
                type,
                value
              ),
            }}
          />
        );
      },
    ],

    [
      ELogType.PROMO_CAMPAIGN_UPDATED,
      (row, t, currency) => {
        const campaign = row?.original?.promo_campaign;

        let type = '';
        let value = '';

        if (campaign?.value) {
          try {
            const valueData = JSON.parse(campaign.value);
            type = getTitleTypeForPromocode(valueData.type, t);

            if (valueData.type === 'DISCOUNT') {
              value = `<b>${valueData.value}%</b>`;
            } else if (valueData.type === 'DEPOSIT_VALUE') {
              value = `<b>${valueData.value}${currency.sign}</b>`;
            } else {
              value = `<b>${valueData.value}⊙</b>`;
            }
          } catch (e) {
            console.error('Error parsing promo value', e);
          }
        }

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t(
                'log.PROMO_CAMPAIGN_UPDATED.description',
                `«<b>${campaign?.title || ''}</b>»`,
                type,
                value
              ),
            }}
          />
        );
      },
    ],
    [
      ELogType.PROMO_CAMPAIGN_DELETED,
      (row, t) => (
        <div
          dangerouslySetInnerHTML={{
            __html: t(
              'log.PROMO_CAMPAIGN_DELETED.description',
              `«<b>${row?.original?.promo_campaign?.title}</b>»`
            ),
          }}
        />
      ),
    ],
    [
      ELogType.PROMO_CAMPAIGN_CODES_GENERATED,
      (row, t) => {
        let settings = null;
        const generationSettings =
          row?.original?.promo_campaign?.generation_settings;

        if (generationSettings) {
          try {
            settings = JSON.parse(generationSettings);
          } catch (e) {
            console.error('Failed to parse generation_settings:', e);
          }
        }

        const getFormat = (value: string): string => {
          switch (value) {
            case 'SYMBOLS':
              return t('promo_group.generate.mask_2');
            case 'NUMERIC':
              return t('promo_group.generate.mask_3');
            case 'MIX':
              return t('promo_group.generate.mask_1');
            default:
              return '';
          }
        };

        const safeSettings = settings || {};
        const safeLength = safeSettings?.length ?? '';
        const safePrefix = safeSettings?.prefix ?? '';
        const safeType = getFormat(safeSettings?.type) ?? '';

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t(
                'log.PROMO_CAMPAIGN_CODES_GENERATED.description',
                `«<b>${row?.original?.promo_campaign?.title || ''}</b>»`,
                `«<b>${row?.original?.value1 || ''}</b>»`,
                `«<b>${safeLength}</b>»`,
                `«<b>${safePrefix}</b>»`,
                `«<b>${safeType}</b>»`
              ),
            }}
          />
        );
      },
    ],
    [
      ELogType.PROMO_CODE_EXPIRED,
      (row, t) => (
        <div
          dangerouslySetInnerHTML={{
            __html: t(
              'log.PROMO_CODE_EXPIRED.description',
              `<b>${row?.original?.promo_code_item?.title}</b>`
            ),
          }}
        />
      ),
    ],
    [
      ELogType.PROMO_CAMPAIGN_EXPIRED,
      (row, t) => (
        <div
          dangerouslySetInnerHTML={{
            __html: t(
              'log.PROMO_CAMPAIGN_EXPIRED.description',
              `<b>${row?.original?.promo_campaign?.title}</b>`
            ),
          }}
        />
      ),
    ],
    [
      ELogType.PROMO_CODE_USED,
      (row, t, currency) => {
        const promo = row?.original?.promo_code_item;
        const client = row?.original?.client;
        const clientText = `<b>${client?.nickname ? client?.nickname : ''} (${
          client?.phone
        })</b>`;
        let type = '';
        let value = '';

        if (promo?.value) {
          try {
            const valueData = JSON.parse(promo?.value);
            type = getTitleTypeForPromocode(valueData.type, t, 'secondary');

            if (valueData.type === 'DISCOUNT') {
              value = `<b>${valueData.value}%</b>`;
            } else if (valueData.type === 'DEPOSIT_VALUE') {
              value = `<b>${valueData.value}${currency.sign}</b>`;
            } else {
              value = `<b>${valueData.value}⊙</b>`;
            }
          } catch (e) {
            console.error('Error parsing promo value', e);
          }
        }

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t(
                'log.PROMO_CODE_USED.description',
                `${client ? clientText : ' '}`,
                `«<b>${promo?.title || ''}</b>»`,
                type,
                value
              ),
            }}
          />
        );
      },
    ],
    [
      ELogType.TARIFF_CREATED,
      (row, t) => {
        const tariffName = `<b>${row?.original?.tariff?.title}</b>`;
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t('log.TARIFF_CREATED.description', tariffName),
            }}
          />
        );
      },
    ],
    [
      ELogType.TARIFF_UPDATED,
      (row, t) => {
        const tariffName = `<b>${row?.original?.tariff?.title}</b>`;
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t('log.TARIFF_UPDATED.description', tariffName),
            }}
          />
        );
      },
    ],
    [
      ELogType.TARIFF_DELETED,
      (row, t) => {
        const tariffName = `<b>${row?.original?.tariff?.title}</b>`;
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t('log.TARIFF_DELETED.description', tariffName),
            }}
          />
        );
      },
    ],
    [
      ELogType.SERVICE_CREATED,
      (row, t) => {
        const servicesName = `<b>${row?.original?.service?.title}</b>`;
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t('log.SERVICE_CREATED.description', servicesName),
            }}
          />
        );
      },
    ],
    [
      ELogType.SERVICE_UPDATED,
      (row, t) => {
        const servicesName = `<b>${row?.original?.service?.title}</b>`;
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t('log.SERVICE_UPDATED.description', servicesName),
            }}
          />
        );
      },
    ],
    [
      ELogType.SERVICE_DELETED,
      (row, t) => {
        const servicesName = `<b>${row?.original?.service?.title}</b>`;
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t('log.SERVICE_DELETED.description', servicesName),
            }}
          />
        );
      },
    ],
    [
      ELogType.CLIENT_SESSION_STARTED,
      (row, t, currency, postPaymentMaxDuration) => {
        const sessionId = `<b>№${row?.original?.client_session.id}</b>`;
        const duration = `<b>${getTimeText(row?.original?.duration, t)}</b>`;
        const finishAtDay = `${dayjs(
          row?.original?.client_session?.finished_at
        ).format('DD.MM')}`;
        const finishAtTime = `<b>${dayjs(
          row?.original?.client_session?.finished_at
        ).format('HH:mm:ss')}</b>`;
        const isClient = Boolean(row?.original?.subject?.uuid);
        const client = row?.original?.client;
        const isPerMinute =
          row?.original?.client_session?.is_per_minute === true;
        const maxFinishAtPost = dayjs(row?.original?.timestamp).add(
          postPaymentMaxDuration,
          'minutes'
        );
        const deposit = row?.original?.deposit;
        const clientText = `<b>${client?.nickname ? client?.nickname : ''} (${
          client?.phone
        })</b>`;
        const clientText_2 = `<b>${client?.nickname ? client?.nickname : ''} (${
          client?.phone
        }, ${deposit}${currency?.sign})</b>`;
        let html = '';
        if (isPerMinute) {
          html = isClient
            ? t(
                'log.CLIENT_SESSION_STARTED.description_2',
                sessionId,
                clientText_2
              )
            : t(
                'log.CLIENT_SESSION_STARTED.description_2.post',
                sessionId,
                maxFinishAtPost.format('DD.MM'),
                `<b>${maxFinishAtPost.format('HH:mm')}</b>`
              );
        } else {
          html = isClient
            ? t(
                'log.CLIENT_SESSION_STARTED.description',
                sessionId,
                clientText,
                duration,
                finishAtDay,
                finishAtTime
              )
            : t(
                'log.CLIENT_SESSION_STARTED.description_guest',
                sessionId,
                duration,
                finishAtDay,
                finishAtTime
              );
        }

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: html,
            }}
          />
        );
      },
    ],
    [
      ELogType.CLIENT_SESSION_PROLONGED,
      (row, t) => {
        const sessionId = `<b>№${row?.original?.client_session.id}</b>`;
        const duration = `<b>${getTimeText(
          row?.original?.additional_seconds,
          t
        )}</b>`;
        const finishAtDay = `${dayjs(
          row?.original?.client_session?.finished_at
        ).format('DD.MM')}`;
        const finishAtTime = `<b>${dayjs(
          row?.original?.client_session?.finished_at
        ).format('HH:mm:ss')}</b>`;
        const isClient = Boolean(row?.original?.subject?.uuid);
        const client = row?.original?.client;
        const clientText = `<b>${client?.nickname ? client?.nickname : ''} (${
          client?.phone
        })</b>`;

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: `${
                isClient
                  ? t(
                      'log.CLIENT_SESSION_PROLONGED.description',
                      sessionId,
                      clientText,
                      duration,
                      finishAtDay,
                      finishAtTime
                    )
                  : t(
                      'log.CLIENT_SESSION_PROLONGED.description_guest',
                      sessionId,
                      duration,
                      finishAtDay,
                      finishAtTime
                    )
              }`,
            }}
          />
        );
      },
    ],
    [
      ELogType.CLIENT_SESSION_HOST_CHANGED,
      (row, t) => {
        const sessionId = `<b>№${row?.original?.client_session.id}</b>`;
        const host1 = `<span style="color: var(--text-secondary)">${row?.original?.host}</span>`;
        const host2 = `<b>${row?.original?.host2?.title}</b>`;

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t(
                'log.CLIENT_SESSION_HOST_CHANGED.description',
                sessionId,
                host1,
                host2
              ),
            }}
          />
        );
      },
    ],
    [
      ELogType.BOOKING_CONFIRMED,
      (row, t) => {
        const bookingId = `<b>№${row?.original?.booking?.id}</b>`;
        const date = `<b>${dayjs(row?.original?.booking?.from).format(
          'DD.MM.YYYY'
        )}</b>`;
        const timePeriod = `<b>${dayjs(row?.original?.booking?.from).format(
          'HH:mm:ss'
        )} - ${dayjs(row?.original?.booking?.to).format('HH:mm:ss')}</b>`;

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t(
                'log.BOOKING_CONFIRMED.description',
                bookingId,
                date,
                timePeriod
              ),
            }}
          />
        );
      },
    ],
    [
      ELogType.SHELL_HIGH_ACCESS_DISABLED,
      (row, t) => {
        const isWorker = Boolean(row?.original?.subject);

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: `${
                isWorker
                  ? t('log.SHELL_HIGH_ACCESS_DISABLED.description_3')
                  : t('log.SHELL_HIGH_ACCESS_DISABLED.description_2')
              }`,
            }}
          />
        );
      },
    ],
    [
      ELogType.SHELL_HIGH_ACCESS_ENABLED,
      (row, t) => {
        const isWorker = Boolean(row?.original?.subject);

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: `${
                isWorker
                  ? t('log.SHELL_HIGH_ACCESS_ENABLED.description')
                  : t('log.SHELL_HIGH_ACCESS_ENABLED.description_2')
              }`,
            }}
          />
        );
      },
    ],
    [
      ELogType.SHELL_ENABLED,
      (row, t) => {
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t('log.SHELL_ENABLED.description'),
            }}
          />
        );
      },
    ],
    [
      ELogType.SHELL_DISABLED,
      (row, t) => {
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t('log.SHELL_DISABLED.description_3'),
            }}
          />
        );
      },
    ],
    [
      ELogType.SETTINGS_UPDATED,
      (row, t) => {
        const settingName = `<b>${row?.original?.setting?.alias}</b>`;
        const settingText = `(<span style="text-decoration: line-through; color: var(--text-secondary)">${sanitizeHTML(
          row?.original?.setting?.value
        )}</span> → <b>${sanitizeHTML(row?.original?.setting?.value2)}</b>)`;
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t(
                'log.SETTINGS_UPDATED.description',
                settingName,
                settingText
              ),
            }}
          />
        );
      },
    ],
    [
      ELogType.DEPOSIT_UPDATED,
      (row, t, currency) => {
        const textMain = t(
          row?.original?.object && row?.original?.subject
            ? 'log.DEPOSIT_UPDATED.description_2'
            : 'log.DEPOSIT_UPDATED.description',
          `<span style="color: var(--text-secondary); text-decoration: line-through">${row?.original?.value1} ${currency.sign}</span> -> ${row?.original?.value2} ${currency.sign}`
        );

        const comment = row?.original?.comment
          ? t(
              'log.DEPOSIT_UPDATED.description_3',
              `<span style="color: var(--text-secondary)">${row.original?.comment}</span>`
            )
          : '';

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: `
            <span>${textMain}</span>
            ${comment ? `<span>${comment}</span>` : ''}
          `,
            }}
          />
        );
      },
    ],
    [
      ELogType.CLUB_COMMENT_CREATED,
      (row, t, currency) => {
        const comment = row?.original?.comment;
        const shortenedComment =
          comment && comment.length > 100
            ? `${comment.substring(0, 100)}...`
            : comment;
        const client = row?.original?.client;
        const sessionId = row?.original?.client_session?.id;
        const score = row?.original?.value1;
        const tips = row?.original?.value2;

        const parts = [];

        if (sessionId) {
          if (client) {
            parts.push(
              t('log.CLUB_COMMENT_CREATED.description.2', `<b>${sessionId}</b>`)
            );
          } else {
            parts.push(
              t('log.CLUB_COMMENT_CREATED.description.6', `<b>${sessionId}</b>`)
            );
          }
        }

        if (score) {
          parts.push(
            t('log.CLUB_COMMENT_CREATED.description.3', `<b>${score}</b>`)
          );
        }

        if (comment) {
          parts.push(
            t(
              'log.CLUB_COMMENT_CREATED.description.4',
              `<b>${shortenedComment}</b>`
            )
          );
        }

        if (tips) {
          parts.push(
            t(
              'log.CLUB_COMMENT_CREATED.description.5',
              `<b>${tips}</b> <b>${currency?.sign}</b>`
            )
          );
        }

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: parts.join('</br>'),
            }}
          />
        );
      },
    ],
    [
      ELogType.HOST_ADMIN_CALLED,
      (row, t) => {
        const isClient = Boolean(row?.original?.client);

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: `${
                isClient
                  ? t('log.HOST_ADMIN_CALLED.description')
                  : t('log.HOST_ADMIN_CALLED.description_2')
              }`,
            }}
          />
        );
      },
    ],
    [
      ELogType.HOST_ADMIN_CALL_CHECKED,
      (row, t) => {
        const isClient = Boolean(row?.original?.client);

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: `${
                isClient
                  ? t('log.HOST_ADMIN_CALL_CHECKED.description')
                  : t('log.HOST_ADMIN_CALL_CHECKED.description_2')
              }`,
            }}
          />
        );
      },
    ],
    [
      ELogType.HOST_ADMIN_CALL_FAILED,
      (row, t) => {
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t('log.HOST_ADMIN_CALL_FAILED.description'),
            }}
          />
        );
      },
    ],
    [
      ELogType.TASK_ADDED,
      (row, t) => {
        const task = `<b>${row?.original?.worker_task?.title}</b>`;

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t('log.TASK_ADDED.description', task),
            }}
          />
        );
      },
    ],
    [
      ELogType.TELEGRAM_UNLINKED,
      (row, t) => {
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t('log.TELEGRAM_UNLINKED.description'),
            }}
          />
        );
      },
    ],
    [
      ELogType.TELEGRAM_LINKED,
      (row, t) => {
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t('log.TELEGRAM_LINKED.description'),
            }}
          />
        );
      },
    ],
    [
      ELogType.HOST_SESSION_STARTED,
      (row, t) => {
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t('log.HOST_SESSION_STARTED.description'),
            }}
          />
        );
      },
    ],
    [
      ELogType.HOST_SESSION_FINISHED,
      (row, t) => {
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t('log.HOST_SESSION_FINISHED.description'),
            }}
          />
        );
      },
    ],
    [
      ELogType.SEND_HOST_COMMAND,
      (row, t) => {
        const command = row?.original?.host_command?.title;
        const getCommandText = (command: null | string): string => {
          const value = `<b>${row?.original?.value1}</b>`;
          const host = `<b>${row?.original?.host}</b>`;
          const comment = `<b>${row?.original?.comment}</b>`;

          switch (command) {
            case 'SHUTDOWN':
              return t('log.SEND_HOST_COMMAND.description.SHUTDOWN_COMMAND');
            case 'REBOOT':
              return t('log.SEND_HOST_COMMAND.description.REBOOT_COMMAND');
            case 'ALLOW_ADMIN_MODE':
              return t(
                'log.SEND_HOST_COMMAND.description.ALLOW_ADMIN_MODE_COMMAND'
              );
            case 'DISABLE_SHELL':
              return t(
                'log.SEND_HOST_COMMAND.description.DISABLE_SHELL_COMMAND'
              );
            case 'SEND_WAKE_PACKET':
              return t('log.SEND_HOST_COMMAND.description.SEND_WAKE_PACKET');
            case 'LOGOFF':
              return t('log.SEND_HOST_COMMAND.description.LOGOFF_COMMAND');
            case 'RUN':
              return t(
                'log.SEND_HOST_COMMAND.description.RUN_COMMAND',
                value,
                host
              );
            case 'SERVICE_MODE_ON':
              return t('log.SEND_HOST_COMMAND.description.SERVICE_MODE_ON');
            case 'SERVICE_MODE_OFF':
              return t('log.SEND_HOST_COMMAND.description.SERVICE_MODE_OFF');
            case 'SHOW_MESSAGE':
              return t(
                'log.SEND_HOST_COMMAND.description.SHOW_MESSAGE',
                comment
              );
            case 'SHELL_UPDATE':
              return t('log.SEND_HOST_COMMAND.description.SHELL_UPDATE');
            case 'SHELL_UNINSTALL':
              return t('log.SEND_HOST_COMMAND.description.SHELL_UNINSTALL');
            default:
              return t('log.SEND_HOST_COMMAND.description.unknown');
          }
        };

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: `${getCommandText(command)}`,
            }}
          />
        );
      },
    ],
    [
      ELogType.SEND_RESET_CODE,
      (row, t) => {
        const method = row?.original?.send_code_method;

        const getText = (method: string): string => {
          switch (method) {
            case 'CASCADE':
              return t('log.SEND_RESET_CODE.description.tel');
            case 'EMAIL':
              return t('log.SEND_RESET_CODE.description.email');
            case 'TELEGRAM':
              return t('log.SEND_RESET_CODE.description.tg');
            case 'FLASH_CALL':
              return t('log.SEND_RESET_CODE.description.tel');
            case 'SMS':
              return t('log.SEND_RESET_CODE.description.tel');
            case 'VK':
              return t('log.SEND_RESET_CODE.description.vk');
            default:
              return t('log.SEND_RESET_CODE.description.tel');
          }
        };

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: getText(method),
            }}
          />
        );
      },
    ],
    [
      ELogType.SEND_VERIFY_CODE,
      (row, t) => {
        const method = row?.original?.send_code_method;

        const getText = (method: string): string => {
          switch (method) {
            case 'CASCADE':
              return t('log.SEND_VERIFY_CODE.description.tel');
            case 'EMAIL':
              return t('log.SEND_VERIFY_CODE.description.email');
            case 'TELEGRAM':
              return t('log.SEND_VERIFY_CODE.description.tg');
            case 'FLASH_CALL':
              return t('log.SEND_VERIFY_CODE.description.tel');
            case 'SMS':
              return t('log.SEND_VERIFY_CODE.description.tel');
            case 'VK':
              return t('log.SEND_VERIFY_CODE.description.vk');
            default:
              return t('log.SEND_VERIFY_CODE.description.tel');
          }
        };

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: getText(method),
            }}
          />
        );
      },
    ],
    [
      ELogType.PAYMENT_REFUND,
      (row, t) => {
        const paymentId = `<b>№${row?.original?.payment?.id}</b>`;

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t('log.PAYMENT_REFUND.description', paymentId),
            }}
          />
        );
      },
    ],
    [
      ELogType.DEPOSIT_ADDED_ONLINE,
      (row, t, currency) => {
        const paymentTitle = `<b>${getOnlinePaymentMethodText(
          row?.original?.payment?.title,
          t
        )}</b>`;

        const paymentId = `<b>№${row?.original?.payment?.id}</b>`;
        const paymentValue = row?.original?.payment?.value;
        const paymentText = `<b>${paymentValue} ${currency.sign}</b>`;
        const cashbackText = `<b>${row?.original?.cashback} ${currency.sign}</b>`;
        const transaction = `<b>${row?.original?.payment_transaction?.uuid}</b>`;
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: `
               <span>
                ${t(
                  'log.DEPOSIT_ADDED_ONLINE.description_4',
                  paymentId,
                  paymentText,
                  paymentTitle,
                  transaction
                )}

               </span>
               </br>
               ${
                 row?.original?.cashback
                   ? t('log.DEPOSIT_ADDED_ONLINE.description_3', cashbackText)
                   : ''
               }
              `,
            }}
          />
        );
      },
    ],
    [
      ELogType.PAYMENT_CREATED,
      (row, t, currency) => {
        const paymentId = row?.original?.payment?.id;
        const paymentMethod = row?.original?.payment?.title;
        const paymentValue = `<b>${row?.original?.payment?.value} ${currency?.sign}</b>`;
        const discount = row?.original?.discount;
        const promoCode = row?.original?.promo_code_item;
        const cashback = row?.original?.cashback;
        const comment = row?.original?.comment;
        const client = row?.original?.client;
        const clientText = `<b>${
          client?.nickname ? client?.nickname : ''
        }</b> (${client?.phone})`;

        // Группируем по entity_type
        const itemsByType = {
          tariffs: [] as any[],
          goods: [] as any[],
          services: [] as any[],
          combos: [] as any[],
        };
        (row?.original?.payment_items || []).forEach((item) => {
          switch (item.entity_type) {
            case 'TARIFF':
              itemsByType.tariffs.push(item);
              break;
            case 'GOOD':
              itemsByType.goods.push(item);
              break;
            case 'SERVICE':
              itemsByType.services.push(item);
              break;
            case 'COMBO':
              itemsByType.combos.push(item);
              break;
          }
        });

        const getSectionTotal = (items: any[]): number =>
          items.reduce((sum, item) => sum + (item.sum || 0), 0);

        const sections = {
          tariffs: {
            items: itemsByType.tariffs,
            total: getSectionTotal(itemsByType.tariffs),
          },
          goods: {
            items: itemsByType.goods,
            total: getSectionTotal(itemsByType.goods),
          },
          services: {
            items: itemsByType.services,
            total: getSectionTotal(itemsByType.services),
          },
          combos: {
            items: itemsByType.combos,
            total: getSectionTotal(itemsByType.combos),
          },
        };
        const hasAnyItems =
          sections.tariffs.items.length > 0 ||
          sections.goods.items.length > 0 ||
          sections.services.items.length > 0 ||
          sections.combos.items.length > 0;

        // Выносим функцию рендеринга секции на уровень выше
        const renderSection = (
          title: string,
          items: any[],
          total: number
        ): string => {
          if (items.length === 0) return '';

          const sectionParts: string[] = [];
          sectionParts.push(`<br>${title} <b>${total} ${currency.sign}</b>`);

          sectionParts.push(
            `<ul style="margin: 0; list-style: square outside; padding-left: 25px">`
          );
          items.forEach((item) => {
            sectionParts.push(
              `<li><b>${item.title}</b>, ${t(
                'log.PAYMENT_CREATED.description_17'
              )} <b>${item.amount} ${t('pcs')}</b>, ${t(
                'log.PAYMENT_CREATED.description_18'
              )} <b>${item.sum ? item.sum : 0} ${currency.sign}</b></li>`
            );
          });
          sectionParts.push(`</ul>`);

          return sectionParts.join('');
        };

        const buildHTML = (): string => {
          const parts: string[] = [];

          // Заголовок
          if (!hasAnyItems && row?.original?.payment?.value) {
            // Только пополнение депозита
            parts.push(
              t(
                'log.PAYMENT_CREATED.description_5',
                `<b>№${paymentId}</b>`,
                clientText
              )
            );

            parts.push(`</br>
            ${t(
              'log.PAYMENT_CREATED.description_13',
              paymentValue,
              `<b>${getPaymentMethodText(paymentMethod, t)}</b>`
            )}
            `);

            // Промокод
            if (promoCode?.value) {
              parts.push(`</br>
            ${t(
              'log.PAYMENT_CREATED.description_14',
              `<b>${promoCode?.title}</b>`,
              `<b>${
                JSON.parse(promoCode?.value)?.type === 'DEPOSIT_VALUE'
                  ? JSON.parse(promoCode?.value)?.value + currency?.sign
                  : JSON.parse(promoCode?.value)?.value + '⊙'
              }</b>`
            )}
            `);
            }

            // Кешбэк
            if (cashback) {
              parts.push(
                `<br>${t(
                  'log.PAYMENT_CREATED.description_15',
                  `<b>${cashback}</b>`
                )}`
              );
            }

            // всего бонусов
            if (row?.original?.value1) {
              parts.push(
                `<br>${t(
                  'log.PAYMENT_CREATED.description_16',
                  `<b>${row?.original?.value1}</b>`
                )}`
              );
            }

            // Комментарий
            if (comment) {
              parts.push(
                `<br>${t(
                  'log.PAYMENT_CREATED.description_12'
                )} <b>${comment}</b>`
              );
            }
          } else if (hasAnyItems) {
            const totalSum =
              sections.tariffs.total +
              sections.goods.total +
              sections.services.total +
              sections.combos.total;

            if (client) {
              parts.push(
                t(
                  'log.PAYMENT_CREATED.description_client',
                  `<b>№${paymentId}</b>`,
                  clientText,
                  `<b>${totalSum} ${currency.sign}</b>`
                )
              );
            } else {
              parts.push(
                t(
                  'log.PAYMENT_CREATED.description',
                  `<b>№${paymentId}</b>`,
                  `<b>${totalSum} ${currency.sign}</b>`
                )
              );
            }

            // Метод оплаты
            parts.push(
              ` ${t(
                'log.PAYMENT_CREATED.description_8'
              )} <b>${getPaymentMethodText(paymentMethod, t)}</b>`
            );

            // Тарифы
            if (sections.tariffs.items.length > 0) {
              parts.push(
                renderSection(
                  t('log.PAYMENT_CREATED.description_9'),
                  sections.tariffs.items,
                  sections.tariffs.total
                )
              );
            }

            // Товары
            if (sections.goods.items.length > 0) {
              parts.push(
                renderSection(
                  t('log.PAYMENT_CREATED.description_10'),
                  sections.goods.items,
                  sections.goods.total
                )
              );
            }

            // Услуги
            if (sections.services.items.length > 0) {
              parts.push(
                renderSection(
                  t('log.PAYMENT_CREATED.description_11'),
                  sections.services.items,
                  sections.services.total
                )
              );
            }

            // Комбо-наборы
            if (sections.combos.items.length > 0) {
              parts.push(
                renderSection(
                  t('logs.categories.combos'),
                  sections.combos.items,
                  sections.combos.total
                )
              );
            }

            // Скидка
            if (discount && hasAnyItems) {
              parts.push(
                `<br>${t(
                  'log.PAYMENT_CREATED.description_4',
                  `<b>${discount.title}</b>`,
                  `<b>${discount.value}%</b>`
                )}`
              );
            }

            // Промокод
            if (promoCode?.title) {
              if (promoCode?.value) {
                parts.push(
                  `<br>${t(
                    'log.PAYMENT_CREATED.description.promocode_2',
                    `<b>${promoCode.title}</b>`,
                    `<b>${JSON.parse(promoCode?.value)?.value}%</b>`
                  )}`
                );
              } else {
                parts.push(
                  `<br>${t(
                    'log.PAYMENT_CREATED.description.promocode',
                    `<b>${promoCode.title}</b>`
                  )}`
                );
              }
            }

            // Кешбэк
            if (cashback) {
              parts.push(
                `<br>${t(
                  'log.PAYMENT_CREATED.description.cashback',
                  `<b>${cashback}</b>`
                )}`
              );
            }

            // Комментарий
            if (comment) {
              parts.push(
                `<br>${t(
                  'log.PAYMENT_CREATED.description_12'
                )} <b>${comment}</b>`
              );
            }
          }
          return parts.join('');
        };

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: buildHTML(),
            }}
          />
        );
      },
    ],
    [
      ELogType.WAREHOUSE_GOODS_DISPOSED,
      (row, t) => {
        const goodTitle = `«<b>${row?.original?.warehouse_item?.title}</b>»`;
        const amount = `<b>${row?.original?.warehouse_item?.value} ${t(
          'pcs'
        )}</b> `;
        const lastText = `(<span style="color: var(--text-secondary); text-decoration: line-through">${
          row?.original?.warehouse_item?.value +
          row?.original?.warehouse_item?.value2
        } ${t('pcs')}</span> → <b>${row?.original?.warehouse_item?.value2} ${t(
          'pcs'
        )}</b>)`;

        const comment = row?.original?.comment
          ? t('log.comment', `<b>${row?.original?.comment}</b>`)
          : '';

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: `
            <span>${t(
              'log.WAREHOUSE_GOODS_DISPOSED.description',
              goodTitle,
              amount,
              lastText
            )}</span>
            ${comment ? `</br>${comment}` : ''}
          `,
            }}
          />
        );
      },
    ],
    [
      ELogType.WAREHOUSE_GOODS_ADDED,
      (row, t) => {
        const goodTitle = `«<b>${row?.original?.warehouse_item?.title}</b>»`;
        const amount = `<b>${row?.original?.warehouse_item?.value} ${t(
          'pcs'
        )}</b> `;
        const lastText = `(<span style="color: var(--text-secondary); text-decoration: line-through">${
          row?.original?.warehouse_item?.value2 -
          row?.original?.warehouse_item?.value
        } ${t('pcs')}</span> → <b>${row?.original?.warehouse_item?.value2} ${t(
          'pcs'
        )}</b>)`;

        const comment = row?.original?.comment
          ? t('log.comment', `<b>${row?.original?.comment}</b>`)
          : '';

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: `
            <span>${t(
              'log.WAREHOUSE_GOODS_ADDED.description',
              goodTitle,
              amount,
              lastText
            )}</span>
            ${comment ? `</br>${comment}` : ''}
          `,
            }}
          />
        );
      },
    ],
    [
      ELogType.USER_REGISTERED,
      (row, t) => {
        return (
          <div
            dangerouslySetInnerHTML={{
              __html: t('log.USER_REGISTERED.description'),
            }}
          />
        );
      },
    ],
    [
      ELogType.USER_DISCOUNT_UPDATED,
      (row, t) => {
        const mainText = `<span style="color: var(--text-secondary); text-decoration: line-through">${row?.original?.value1}% </span> → <b>${row?.original?.value2}%</b></br>`;

        const comment = `<b>${row?.original?.comment}</b>`;

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: `
                ${
                  row?.original?.comment
                    ? t(
                        'log.USER_DISCOUNT_UPDATED.description_2',
                        mainText,
                        comment
                      )
                    : t('log.USER_DISCOUNT_UPDATED.description', mainText)
                }
          `,
            }}
          />
        );
      },
    ],
    [
      ELogType.POST_PAYMENT_CANCELED,
      (row, t) => {
        const postId = `<b>№${row?.original?.payment?.id}</b>`;

        const comment = `<b>${row?.original?.comment}</b>`;

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: `${t('log.POST_PAYMENT_CANCELED.description', postId)}
              </br>${
                row?.original?.comment
                  ? t('log.POST_PAYMENT_CANCELED.description_2', comment)
                  : ''
              }
          `,
            }}
          />
        );
      },
    ],
    [
      ELogType.USER_GROUP_CREATED,
      (row, t) => {
        const groupTitle = `<b>«${row?.original?.user_group?.title}»</b>`;

        const value = `<b>${row?.original?.user_group?.value}</b>`;

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: `${t(
                'log.USER_GROUP_CREATED.description',
                groupTitle,
                value
              )}
          `,
            }}
          />
        );
      },
    ],
    [
      ELogType.USER_GROUP_DELETED,
      (row, t) => {
        const groupTitle = `<b>«${row?.original?.user_group?.title}»</b>`;

        const value = `<b>${row?.original?.user_group?.value}</b>`;

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: `${t(
                'log.USER_GROUP_DELETED.description',
                groupTitle,
                value
              )}
          `,
            }}
          />
        );
      },
    ],
    [
      ELogType.USER_GROUP_UPDATED,
      (row, t) => {
        const groupTitle1 = `<span style="color: var(--text-secondary); text-decoration: line-through">${row?.original?.user_group2?.title}</span>`;
        const groupTitle2 = `<b>${row?.original?.user_group?.title}</b>`;

        const value1 = `<span style="color: var(--text-secondary); text-decoration: line-through">${row?.original?.user_group2?.value}%</span>`;
        const value2 = `<b>${row?.original?.user_group?.value}</b>`;

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: `${t(
                'log.USER_GROUP_UPDATED.description',
                groupTitle1,
                groupTitle2
              )}</br>${t(
                'log.USER_GROUP_UPDATED.description_2',
                value1,
                value2
              )}
          `,
            }}
          />
        );
      },
    ],
    [
      ELogType.USER_CHANGED_GROUP,
      (row, t) => {
        const groupTitle = row?.original?.user_group?.title || '—';
        const clientsHTML = (row?.original?.additional_data ?? '').trim();

        let html = t(
          'log.USER_CHANGED_GROUP.added',
          `<b>${groupTitle}</b>`,
          clientsHTML || '—'
        );

        if (!/[.!?]$/.test(html)) html += '.';

        return <span dangerouslySetInnerHTML={{ __html: html }} />;
      },
    ],
    [
      ELogType.USER_VERIFIED,
      (row, t) => (
        <div
          dangerouslySetInnerHTML={{
            __html: t('log.USER_VERIFIED.description'),
          }}
        />
      ),
    ],
    [
      ELogType.NEWS_CREATED,
      (row, t) => {
        const title = row?.original?.company_news_item?.title;
        const isPublic = row?.original?.company_news_item?.active
          ? `${t('log.NEWS_CREATED.public')} <b>${t(
              'notifyService.confirm.accept'
            )}</b>`
          : `${t('log.NEWS_CREATED.public')} <b>${t('no')}</b>`;

        const period = row?.original?.company_news_item?.active_from
          ? `${t('log.NEWS_CREATED.period')} <b>${dayjs(
              row?.original?.company_news_item?.active_from
            ).format('DD.MM HH:mm:ss')} — ${dayjs(
              row?.original?.company_news_item?.active_to
            ).format('DD.MM HH:mm:ss')}</b>`
          : `${t('log.NEWS_CREATED.period')} <b>${t('no')}</b>`;

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: `
            ${t('log.NEWS_CREATED.description', title)}
            <br/>
            ${isPublic}
            </br>
            ${period}
          `,
            }}
          />
        );
      },
    ],
    [
      ELogType.NEWS_UPDATED,
      (row, t) => {
        const title = row?.original?.company_news_item?.title;

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: `
            ${t('log.NEWS_UPDATED', title)}
            
          `,
            }}
          />
        );
      },
    ],
    [
      ELogType.NEWS_DELETED,
      (row, t) => {
        const title = `${row?.original?.company_news_item?.title}`;

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: `
            ${t('log.NEWS_DELETED', title)}
          `,
            }}
          />
        );
      },
    ],
    [
      ELogType.HOST_QR_GENERATE,
      (row, t) => (
        <div
          dangerouslySetInnerHTML={{
            __html: t(
              'log.HOST_QR_GENERATE.description',
              `<b>${row?.original?.host}</b>`
            ),
          }}
        />
      ),
    ],
    [
      ELogType.SHORTCUT_CREATED,
      (row, t) => {
        const title = row?.original?.shortcut?.title;

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: `
            ${t('log.SHORTCUT_CREATED.description', title)}
          `,
            }}
          />
        );
      },
    ],
    [
      ELogType.SHORTCUT_UPDATED,
      (row, t) => {
        const title = JSON.parse(row?.original?.additional_data).title_old;

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: `
            ${t('log.SHORTCUT_UPDATE.description', title)}
          `,
            }}
          />
        );
      },
    ],
    [
      ELogType.SHORTCUT_DELETED,
      (row, t) => {
        const title = row?.original?.shortcut?.title;

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: `
            ${t('log.SHORTCUT_DELETE.description', title)}
          `,
            }}
          />
        );
      },
    ],
    [
      ELogType.USER_DEPOSIT_TRANSFERRED,
      (row, t, currency) => {
        interface AdditionalData {
          club_name?: string;
          club_id?: number;
          add?: boolean;
        }

        let data: AdditionalData = {};
        try {
          data = JSON.parse(row?.original?.additional_data || '{}');
        } catch (e) {
          console.error('Failed to parse additional_data:', e);
          data = {};
        }

        const clubName = `<b>${data?.club_name || ''}</b>`;
        const clubId = `<b>${data?.club_id || ''}</b>`;
        const operating = data?.add;

        const value1 = Number(row?.original?.value1) || 0;
        const value2 = Number(row?.original?.value2) || 0;

        const depositStart = `<span style="color: var(--text-secondary); text-decoration: line-through">${value1} ${
          currency?.sign || ''
        }</span>`;
        const delta = `<b>${value2} ${currency?.sign || ''}</b>`;
        const depositResult = `<b>${
          operating ? value1 + value2 : value1 - value2
        } ${currency?.sign || ''}</b>`;

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: `
        ${t('log.USER_DEPOSIT_TRANSFERRED.description')}
        ${
          row?.original?.additional_data !== ''
            ? `<br/><br/>${
                operating
                  ? t(
                      'log.USER_DEPOSIT_TRANSFERRED.description_3',
                      delta,
                      clubName,
                      clubId,
                      depositStart,
                      depositResult
                    )
                  : t(
                      'log.USER_DEPOSIT_TRANSFERRED.description_2',
                      delta,
                      clubName,
                      clubId,
                      depositStart,
                      depositResult
                    )
              }`
            : ''
        }
      `,
            }}
          />
        );
      },
    ],
    [
      ELogType.SETTINGS_COPIED,
      (row, t) => {
        const mainClub = `<b>${row?.original?.additional_data}</b>`;
        const childClub = `<b>${row?.original?.comment}</b>`;

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: `
            ${t('log.SETTINGS_COPIED.description', mainClub, childClub)}
          `,
            }}
          />
        );
      },
    ],
    [
      ELogType.ADVERTISEMENT_MODERATION,
      (row, t) => {
        const title = row?.original?.advertisement?.title;

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: `
            ${t('log.ADVERTISEMENT_MODERATION.description', title)}
          `,
            }}
          />
        );
      },
    ],
    [
      ELogType.ADVERTISEMENT_REJECTED,
      (row, t) => {
        const title = row?.original?.advertisement?.title;

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: `
            ${t('log.ADVERTISEMENT_REJECTED.description', title)}
          `,
            }}
          />
        );
      },
    ],
    [
      ELogType.ADVERTISEMENT_APPROVED,
      (row, t) => {
        const title = row?.original?.advertisement?.title;

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: `
            ${t('log.ADVERTISEMENT_APPROVED.description', title)}
          `,
            }}
          />
        );
      },
    ],
    [
      ELogType.ADVERTISEMENT_MANUAL_COMPLETED,
      (row, t) => {
        const title = row?.original?.advertisement?.title;

        return (
          <div
            dangerouslySetInnerHTML={{
              __html: `
            ${t('log.ADVERTISEMENT_MANUAL_COMPLETED.description', title)}
          `,
            }}
          />
        );
      },
    ],
  ]
);
